import React from 'react';
import { ExternalLink, ArrowRight, Rocket, HardHat, Wrench, Droplets, Building, Zap, Handshake } from 'lucide-react';
import { motion } from 'framer-motion';
import { aedisPlatform } from '../../data/mock';
import { useLanguage } from '../../context/LanguageContext';

const ICONS = { HardHat, Wrench, Droplets, Building, Zap, Handshake };

/**
 * Featured platform section for Smartland subsidiary — showcases AEDIS.
 * Infrastructure engineering & development with Space Ports vertical.
 */
const AEDISSection = () => {
  const { pick } = useLanguage();
  const orange = aedisPlatform.brandColor;
  const aubergine = aedisPlatform.accentColor;

  return (
    <section
      className="section-padding-lg border-t relative overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #16100c 0%, #1a1311 100%)',
        borderColor: `${orange}40`
      }}
      data-testid="aedis-section"
    >
      {/* Ambient earth-tone glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 right-0 w-[700px] h-[700px] rounded-full blur-3xl opacity-25"
          style={{ background: `radial-gradient(circle, ${orange}60 0%, transparent 70%)` }}
        />
        <div
          className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full blur-3xl opacity-20"
          style={{ background: `radial-gradient(circle, ${aubergine}80 0%, transparent 70%)` }}
        />
      </div>

      <div className="container-swg relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <span
            className="inline-block px-5 py-2 text-[11px] tracking-[0.25em] uppercase font-medium"
            style={{ color: orange, border: `1px solid ${orange}60`, background: `${orange}10` }}
            data-testid="aedis-eyebrow"
          >
            {pick(aedisPlatform.eyebrow)}
          </span>

          <div className="mt-8 flex items-center justify-center gap-5 flex-wrap">
            <img
              src={aedisPlatform.logo}
              alt="AEDIS"
              className="h-16 w-auto object-contain drop-shadow-[0_0_24px_rgba(200,97,42,0.5)]"
              data-testid="aedis-logo"
            />
            <h2 className="text-5xl md:text-6xl font-black tracking-tight text-white">AEDIS</h2>
          </div>

          <p className="mt-6 text-xl md:text-2xl text-white font-light tracking-wide">
            {pick(aedisPlatform.tagline)}
          </p>
          <p className="mt-2 italic text-base" style={{ color: orange }}>
            {pick(aedisPlatform.signature)}
          </p>
        </motion.div>

        {/* Preview + brief */}
        <div className="grid lg:grid-cols-5 gap-10 items-center mb-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-3"
          >
            <a
              href="#" onClick={(e) => e.preventDefault()} aria-disabled="true"
              className="block relative group overflow-hidden border shadow-[0_20px_60px_-20px_rgba(200,97,42,0.5)]"
              style={{ borderColor: `${orange}50` }}
              data-testid="aedis-preview-link"
            >
              <img
                src={aedisPlatform.preview}
                alt="AEDIS platform preview"
                className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                loading="lazy"
              />
              <div
                className="absolute bottom-4 right-4 px-4 py-2 backdrop-blur-md text-xs font-medium flex items-center gap-2 opacity-90 group-hover:opacity-100 transition-opacity tracking-widest uppercase"
                style={{ background: `${aubergine}cc`, border: `1px solid ${orange}`, color: orange }}
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Live
              </div>
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-2"
          >
            <p className="text-lg text-white leading-relaxed mb-5">{pick(aedisPlatform.shortBrief)}</p>
            <p className="text-[#9AACB8] leading-relaxed mb-8 text-sm">{pick(aedisPlatform.longBrief)}</p>

            <a
              href="#" onClick={(e) => e.preventDefault()} aria-disabled="true"
              className="inline-flex items-center gap-3 px-10 py-4 font-semibold text-sm tracking-[0.2em] uppercase transition-all duration-300 hover:scale-[1.03]"
              style={{
                background: orange,
                color: '#ffffff',
                boxShadow: `0 8px 30px ${orange}50`
              }}
              data-testid="aedis-cta"
            >
              {pick(aedisPlatform.cta)}
              <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>

        {/* 6 expertise domains */}
        <div className="mb-14">
          <h3 className="text-center text-[#9AACB8] uppercase tracking-[0.3em] text-xs mb-8 font-mono">
            {pick({ en: 'Six expertise domains', fr: "Six domaines d'expertise" })}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {aedisPlatform.domains.map((domain, index) => {
              const Icon = ICONS[domain.icon] || Building;
              return (
                <motion.div
                  key={domain.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.06 }}
                  className="p-5 flex items-center gap-4"
                  style={{ background: `${aubergine}15`, border: `1px solid ${orange}30` }}
                  data-testid={`aedis-domain-${domain.id}`}
                >
                  <div
                    className="w-11 h-11 flex items-center justify-center flex-shrink-0"
                    style={{ background: `${orange}20`, border: `1px solid ${orange}50` }}
                  >
                    <Icon className="w-5 h-5" style={{ color: orange }} />
                  </div>
                  <span className="text-white text-sm font-medium leading-tight">{pick(domain.name)}</span>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Space Ports — strategic vertical */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-4xl mx-auto rounded-none p-10 relative overflow-hidden"
          style={{
            background: `linear-gradient(135deg, ${aubergine}40 0%, ${orange}15 100%)`,
            border: `1px solid ${orange}`
          }}
          data-testid="aedis-vertical"
        >
          {/* Subtle orbit lines */}
          <div className="absolute inset-0 pointer-events-none opacity-20">
            <div className="absolute -top-1/2 -right-1/4 w-[600px] h-[600px] rounded-full border" style={{ borderColor: orange }} />
            <div className="absolute -top-1/3 -right-1/3 w-[400px] h-[400px] rounded-full border" style={{ borderColor: orange }} />
          </div>

          <div className="flex items-start gap-5 relative z-10">
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: `${orange}25`, border: `1px solid ${orange}60` }}
            >
              <Rocket className="w-7 h-7" style={{ color: orange }} />
            </div>
            <div className="flex-1">
              <span
                className="inline-block text-[10px] uppercase tracking-[0.3em] mb-3 font-mono"
                style={{ color: orange }}
              >
                {pick(aedisPlatform.vertical.badge)}
              </span>
              <h3 className="text-white font-black text-3xl tracking-tight mb-3">
                {aedisPlatform.vertical.name}
              </h3>
              <p className="text-[#c9bdb0] leading-relaxed">{pick(aedisPlatform.vertical.desc)}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AEDISSection;
